import { insuranceBillingKind } from '@/types/insurance';
import { UserRole } from '@/types/user';

export type WaiverDecision = 'approved_100' | 'approved_50' | 'denied';
export type AssistanceMode = 'standard' | 'ccap';

export type AssistanceGate =
  | { kind: 'already'; percent: 50 | 100 }
  | { kind: 'ccap' }
  | { kind: 'self_pay' }
  | { kind: 'qmb' }
  | { kind: 'apply' };

const HOUSEHOLD_BANDS: { people: number; full: number; half: number }[] = [
  { people: 1, full: 45180, half: 60240 },
  { people: 2, full: 61320, half: 81760 },
  { people: 3, full: 77460, half: 103280 },
  { people: 4, full: 93600, half: 124800 },
  { people: 5, full: 109740, half: 146320 }
];

/** Same income bands as the Salesforce financial assistance flow. */
export function assistanceDecision(income: number, people: number): WaiverDecision {
  if (!Number.isFinite(income) || income < 0 || !Number.isInteger(people) || people < 1) {
    return 'denied';
  }
  const band = HOUSEHOLD_BANDS.find((row) => row.people === people);
  const full = band ? band.full : 41880 + (people - 1) * 16740;
  const half = band ? band.half : 60240 + (people - 1) * 20800;
  if (income <= full) return 'approved_100';
  if (income <= half) return 'approved_50';
  return 'denied';
}

/**
 * Override to store. A standard denial leaves the current value in place, matching
 * the Salesforce flow: PatientDenied sets assistance status to Denied and does not
 * change Financial_Payer_Override__c. A CCAP denial writes CCAP.
 */
export function assistanceOverride(decision: WaiverDecision, mode: AssistanceMode): string | null {
  if (mode === 'ccap') {
    if (decision === 'approved_100') return 'CCAP100';
    if (decision === 'approved_50') return 'CCAP50';
    return 'CCAP';
  }
  if (decision === 'approved_100') return 'P100';
  if (decision === 'approved_50') return 'P50';
  return null;
}

export function assistanceGate(override?: string | null): AssistanceGate {
  const value = (override ?? '').trim();
  if (value === 'P100') return { kind: 'already', percent: 100 };
  if (value === 'P50') return { kind: 'already', percent: 50 };
  if (value === 'CCAP' || value === 'CCAP50' || value === 'CCAP100') return { kind: 'ccap' };
  if (value === 'SELF-PAY') return { kind: 'self_pay' };
  if (value === 'QMB') return { kind: 'qmb' };
  return { kind: 'apply' };
}

export function parseMoney(value: string): number | null {
  const cleaned = value.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const amount = Number(cleaned);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return amount;
}

export function parsePeople(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const people = Number(value);
  return people >= 1 ? people : null;
}

export function showsFinancialAssistance(billingType?: string | null): boolean {
  return insuranceBillingKind(billingType) === 'split bill';
}

export function canEditFinancialOverride(role?: string | null): boolean {
  return role === UserRole.InternalAdmin || role === UserRole.InternalSuperAdmin;
}

export function decisionMessage(
  decision: WaiverDecision,
  mode: AssistanceMode
): { title: string; body: string } {
  if (decision === 'approved_100') {
    return {
      title: 'Approved 100% waiver',
      body: 'The applicant meets the criteria for financial assistance. They will receive a 100% waiver of cost share from Rhythm, so they will not receive a bill from us. Insurance is still billed for the services.'
    };
  }
  if (decision === 'approved_50') {
    return {
      title: 'Approved 50% waiver',
      body:
        mode === 'ccap'
          ? 'The applicant meets the criteria for financial assistance. They will receive a 50% waiver of cost share from Rhythm, so the bill should be no more than $12.50 per encounter.'
          : 'The applicant meets the criteria for financial assistance. They will receive a 50% waiver of cost share from Rhythm, so the bill should be about $15–20 per encounter.'
    };
  }
  return {
    title: 'Denied',
    body:
      mode === 'ccap'
        ? 'The applicant does not meet the criteria for a further discount. Cost share stays at the $25 cap. Offer Self-Pay or a monitoring-frequency change if they do not want to continue.'
        : 'The applicant does not meet the criteria for financial assistance. Cost share is not discounted. Offer Self-Pay or a monitoring-frequency change if they do not want to continue.'
  };
}
